import { Formik, FieldArray } from 'formik';

import { Company } from 'types';
import { Messages } from 'config';
import { Flex } from 'components/flex';
import { Row, Col } from 'components/grid';
import * as CommonStyles from 'components/styles';
import { FormFooter } from 'components/form-footer';
import { Button, ButtonTypes } from 'components/button';
import { handleFormSubmitWithFiles } from 'utils/helpers';
import { SectionHeader } from 'components/section-header';
import { useAlert, useNavigate, handleMutation } from 'hooks';
import { MyCompanyInfo, useEditMutation } from 'store/api/company';
import { FormPhotoFieldRow } from 'components/form-photo-field-row';
import { ChangePasswordDialog } from 'components/change-password-dialog';
import {
  InputField,
  SwitchField,
  StatesSelectField,
  AddressAutoCompleteInputField,
} from 'components/fields';

import * as Styles from './styles';
import { ChangeEmailDialog } from './change-email';
import { PayrollSettingsSection } from './payroll-settings';
import { FormValues, CompanyBusinessFormProps } from './types';
import {
  getInitialValues,
  validationSchema,
  getUnderMinimumMethodPayload,
} from './config';

const isMyCompanyInfo = (
  data?: Company | MyCompanyInfo
): data is MyCompanyInfo => (data as MyCompanyInfo).companyOwner !== undefined;

export const CompanyBusinessForm = ({
  data,
  hasNavigate,
}: CompanyBusinessFormProps) => {
  const navigate = useNavigate();
  const { handleError, showSuccessAlert } = useAlert();

  const [update, mutation] = useEditMutation();
  handleMutation(mutation);

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      initialValues={getInitialValues(data)}
      onSubmit={handleFormSubmitWithFiles({
        data,
        handleError,
        files: [
          {
            formFieldName: 'photo',
            dataFieldName: 'photoUrl',
            requestFieldName: 'photoUrl',
          },
        ],
        onSubmit: async (values) => {
          const response = await update({
            ...values,
            id: String(data?.id),
            // Set by the server when Under Minimum is switched on; never sent.
            underMinimumEnabledAt: undefined,
            ...getUnderMinimumMethodPayload(values),
            processingFee: Number(values.processingFee),
            tipFeePercent: Number(values.tipFeePercent),
            brokenMissingPercent: Number(values.brokenMissingPercent),
            underMinimumThreshold: Number(values.underMinimumThreshold),
            tipOptions: values.tipOptions?.map(Number)?.filter(Boolean),
            notificationEmails: values?.notificationEmails?.filter(Boolean),
            notificationPhones: values?.notificationPhones?.filter(Boolean),
          }).unwrap();

          if (response.id) {
            showSuccessAlert(
              `Company settings ${Messages.UPDATED_SUCCESSFULLY}`
            );

            if (hasNavigate) {
              navigate(-1);
            }
          }
        },
      })}
    >
      {({ dirty, values, isSubmitting }) => (
        <CommonStyles.Form $hasMinHeight padding="16px">
          <Styles.Container>
            <FormPhotoFieldRow />

            <SectionHeader withBorder={false} title="General info" />
            <Row>
              <Col col={3}>
                <InputField
                  name="name"
                  label="Company name"
                  placeholder="Company name"
                />
              </Col>
              <Col col={3}>
                <InputField
                  name="businessOwner"
                  label="Business owner"
                  placeholder="Business owner"
                />
              </Col>
              <Col col={3}>
                <InputField
                  name="website"
                  label="Website"
                  placeholder="Website"
                />
              </Col>
            </Row>

            <Row>
              <Col col={3}>
                <InputField name="email" label="Email" placeholder="Email" />
              </Col>
              <Col col={3}>
                <InputField
                  type="tel"
                  name="phone"
                  label="Company phone"
                  placeholder="Company phone"
                />
              </Col>
              <Col col={3}>
                <InputField name="fax" label="Fax" placeholder="Fax" />
              </Col>
            </Row>

            <SectionHeader title="Address" withBorder={false} />
            <Row>
              <Col col={3}>
                <AddressAutoCompleteInputField />
              </Col>
              <Col col={3}>
                <StatesSelectField isDisabled name="state" />
              </Col>
              <Col col={3}>
                <InputField
                  number
                  disabled
                  name="zip"
                  label="Zip code"
                  placeholder="Zip code"
                />
              </Col>
              <Col col={3}>
                <InputField
                  disabled
                  name="city"
                  label="City"
                  placeholder="City"
                />
              </Col>
            </Row>

            <SectionHeader
              withBorder={false}
              title="Additional Notifications"
            />
            <FieldArray
              name="notificationEmails"
              render={({ push, remove }) => (
                <>
                  <Row>
                    {values.notificationEmails?.map((_, idx) => (
                      <Col col={3} key={idx}>
                        <Flex gap={8} alignItems="center">
                          <InputField
                            placeholder="Email"
                            label={`Email ${idx + 1}`}
                            name={`notificationEmails.${idx}`}
                          />

                          {idx > 0 && (
                            <Styles.StyledDeleteButton
                              onClick={() => remove(idx)}
                            />
                          )}
                        </Flex>
                      </Col>
                    ))}
                  </Row>

                  <Row>
                    <Col>
                      <Styles.StyledAddButton
                        text="Add email"
                        onClick={() => push('')}
                      />
                    </Col>
                  </Row>
                </>
              )}
            />

            <FieldArray
              name="notificationPhones"
              render={({ push, remove }) => (
                <>
                  <Row>
                    {values.notificationPhones?.map((_, idx) => (
                      <Col col={3} key={idx}>
                        <Flex gap={8} alignItems="center">
                          <InputField
                            type="tel"
                            placeholder="Phone"
                            label={`Phone ${idx + 1}`}
                            name={`notificationPhones.${idx}`}
                          />

                          {idx > 0 && (
                            <Styles.StyledDeleteButton
                              onClick={() => remove(idx)}
                            />
                          )}
                        </Flex>
                      </Col>
                    ))}
                  </Row>

                  <Row>
                    <Col>
                      <Styles.StyledAddButton
                        text="Add phone"
                        onClick={() => push('')}
                      />
                    </Col>
                  </Row>
                </>
              )}
            />

            {isMyCompanyInfo(data) && (
              <>
                <SectionHeader withBorder={false} title="Account preferences" />
                <Row>
                  <Col col={3}>
                    <Flex gap={8} flexDirection="column">
                      <Styles.Title>Email</Styles.Title>

                      <Flex gap={16}>
                        <h4>{data?.companyOwner?.email}</h4>
                        <ChangeEmailDialog />
                      </Flex>
                    </Flex>
                  </Col>

                  <Col col={3}>
                    <Flex gap={8} flexDirection="column">
                      <Styles.Title>Password</Styles.Title>
                      <ChangePasswordDialog />
                    </Flex>
                  </Col>
                </Row>
              </>
            )}

            <SectionHeader title="Sale" withBorder={false} />
            <Row>
              <Col col={3}>
                <SwitchField
                  label="Enable processing fee"
                  name="isProcessingFeeEnabled"
                />
              </Col>
            </Row>
            {values.isProcessingFeeEnabled && (
              <Row>
                <Col col={4}>
                  <Flex gap={8} flexDirection="column">
                    <InputField
                      number
                      decimal
                      name="processingFee"
                      label="Processing Fee"
                      placeholder="Processing Fee"
                    />
                    <Styles.SwitchSubtitle>
                      Cash discount helps you reduce processing fees. This does
                      not apply to split payment transactions. Be sure to inform
                      your customers you have cash versus credit prices. Consult
                      with your sales partner if you need to change your fixed
                      amount/percentage or would like to participate in the
                      program.
                    </Styles.SwitchSubtitle>
                  </Flex>
                </Col>
              </Row>
            )}

            <SectionHeader title="Tips" withBorder={false} />
            <Row>
              <Col col={3}>
                <SwitchField label="Enable tips" name="isTipsEnabled" />
              </Col>
            </Row>

            {values.isTipsEnabled && (
              <Row>
                <FieldArray
                  name="tipOptions"
                  render={() =>
                    new Array(3).fill('').map((_, idx) => (
                      <Col col={3} key={idx}>
                        <InputField
                          number
                          placeholder="Tip"
                          label={`Tip ${idx + 1}`}
                          controlErrorLabel={false}
                          name={`tipOptions.${idx}`}
                        />
                      </Col>
                    ))
                  }
                />
              </Row>
            )}

            <PayrollSettingsSection companyId={data?.id} />

            <SectionHeader
              withBorder={false}
              title="Under Minimum Sale Request"
            />
            <Row>
              <Col col={3}>
                <SwitchField
                  name="isApprovalRequestEnabled"
                  label="Under Minimum Sale Request"
                />
              </Col>
            </Row>

            <SectionHeader
              withBorder={false}
              title="Display Event Competition"
            />
            <Row>
              <Col col={3}>
                <SwitchField
                  name="isEventCompetitionEnabled"
                  label="Display Event Competition"
                />
              </Col>
            </Row>
          </Styles.Container>
          <FormFooter
            right={
              <Button
                text="Save"
                type={ButtonTypes.Submit}
                disabled={!dirty || isSubmitting}
              />
            }
          />
        </CommonStyles.Form>
      )}
    </Formik>
  );
};
